/**
 * DirectMessageComposeModal — "Send a direct message" trigger used on the
 * Agency detail + Rep detail headers. Not tied to a client. Sends a general
 * question / broadcast to a specific rep, a specific manager, or all reps
 * at an agency.
 *
 * Props:
 *   • agencyId       — required. Scopes the thread and pre-fills the picker.
 *   • agencyName     — for the modal title.
 *   • preselectUser  — optional {id, name, role}. If provided, the modal
 *                      defaults to that user (used on Rep detail).
 *   • trigger        — optional React node to override the default button.
 */
import { useEffect, useRef, useState } from "react";
import { RichTextarea } from "./RichTextarea";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { createDirectThread, uploadFile } from "../lib/api";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, MessageSquarePlus, Users, User, Send, Paperclip, X as XIcon } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DirectMessageComposeModal({
  agencyId, agencyName, preselectUser = null, trigger = null,
}) {
  const [open, setOpen] = useState(false);
  const [recipientType, setRecipientType] = useState(preselectUser ? preselectUser.role : "rep");
  const [userId, setUserId] = useState(preselectUser?.id || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [members, setMembers] = useState([]);  // {id, name, role, email}
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);  // [{id, filename}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Load agency members when the modal opens and we need to pick a rep/manager
  useEffect(() => {
    if (!open || !agencyId) return;
    if (recipientType === "agency_broadcast") return;
    setLoadingMembers(true);
    axios.get(`${API}/agencies/${agencyId}/users`)
      .then((r) => setMembers(Array.isArray(r.data) ? r.data : (r.data?.users || [])))
      .catch(() => setMembers([]))
      .finally(() => setLoadingMembers(false));
  }, [open, agencyId, recipientType]);

  const filteredMembers = members.filter((m) =>
    recipientType === "manager" ? m.role === "manager" : m.role === "rep"
  );

  const canSubmit = (subject.trim() && (body.trim() || attachments.length > 0)) && (
    recipientType === "agency_broadcast" ? !!agencyId : !!userId
  );

  const handleAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 25 MB — skipped`);
          continue;
        }
        const { data } = await uploadFile(file);
        setAttachments((prev) => [...prev, { id: data.id, filename: data.filename }]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((f) => f.id !== id));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        subject: subject.trim(),
        body: body.trim(),
        recipient_type: recipientType,
        agency_id: agencyId,
        file_ids: attachments.map((a) => a.id),
      };
      if (recipientType !== "agency_broadcast") payload.user_id = userId;
      const { data } = await createDirectThread(payload);
      toast.success("Message sent");
      setOpen(false);
      setSubject("");
      setBody("");
      setAttachments([]);
      setUserId(preselectUser?.id || "");
      if (data?.id) navigate(`/direct/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" data-testid="direct-message-trigger-btn">
            <MessageSquarePlus className="mr-2 h-4 w-4" />
            Send message
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="direct-message-modal">
        <DialogHeader>
          <DialogTitle>Send a direct message{agencyName ? ` — ${agencyName}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!preselectUser && (
            <div className="space-y-2">
              <Label>Send to</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "rep", label: "One rep", icon: User },
                  { id: "manager", label: "One manager", icon: User },
                  { id: "agency_broadcast", label: "All reps", icon: Users },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = recipientType === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { setRecipientType(opt.id); setUserId(""); }}
                      data-testid={`direct-msg-recipient-type-${opt.id}`}
                      className={`flex flex-col items-center gap-1 rounded-md border p-3 text-xs font-medium transition ${active ? "border-primary bg-primary/5 text-primary" : "border-zinc-200 bg-white hover:border-primary/40"}`}
                    >
                      <Icon className="h-4 w-4" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {recipientType !== "agency_broadcast" && !preselectUser && (
            <div className="space-y-2">
              <Label>Recipient</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="direct-msg-recipient-picker">
                  <SelectValue placeholder={loadingMembers ? "Loading…" : `Pick a ${recipientType}`} />
                </SelectTrigger>
                <SelectContent>
                  {filteredMembers.length === 0 ? (
                    <div className="px-2 py-2 text-xs text-muted-foreground">
                      No {recipientType}s at this agency.
                    </div>
                  ) : filteredMembers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.email} — {m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {preselectUser && (
            <div className="rounded-md border bg-zinc-50 px-3 py-2 text-xs" data-testid="direct-msg-preselect">
              Sending to <strong>{preselectUser.name}</strong> ({preselectUser.role})
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="direct-msg-subject">Subject</Label>
            <Input
              id="direct-msg-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Quick question about your pipeline"
              data-testid="direct-msg-subject-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="direct-msg-body">Message</Label>
            <RichTextarea
              id="direct-msg-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="Type your message…"
              data-testid="direct-msg-body-input"
            />
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="direct-msg-pending-attachments">
              {attachments.map((f) => (
                <span
                  key={f.id}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary text-[11px] font-medium border"
                  data-testid={`direct-msg-attachment-${f.id}`}
                >
                  <Paperclip className="h-3 w-3" />
                  <span className="max-w-[180px] truncate">{f.filename}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(f.id)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${f.filename}`}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleAttach}
            data-testid="direct-msg-file-input"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || submitting}
            data-testid="direct-msg-attach-btn"
            className="mr-auto"
          >
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />}
            Attach
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || submitting} data-testid="direct-msg-send-btn">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
